/**
 * userStorage.js — user-scoped localStorage wrapper.
 *
 * Every value is stored under `u${userId}_<key>` so data from different
 * accounts can never collide. Guest (not logged-in) sessions use the
 * `guest_<key>` prefix. Switching users causes React to remount providers
 * (via `key={userId}` in App.jsx) which automatically re-reads the correct
 * namespace on init.
 *
 * Usage:
 *   import { userStorage, _setUserId } from '../utils/userStorage';
 *
 *   // In useAuth — call on every login / logout:
 *   _setUserId(user.id);   // string or number
 *   _setUserId(null);      // on logout
 *
 *   // Anywhere in the app:
 *   userStorage.get('binance_api')              // → string | null
 *   userStorage.set('binance_api', apiKey)
 *   userStorage.getJSON('drawings')             // → parsed object | null
 *   userStorage.setJSON('drawings', drawingsMap)
 *   userStorage.remove('binance_api')
 */

let _uid = null;

/**
 * Set the active user id. Call this from useAuth whenever the user
 * changes (login, logout, page boot with existing token).
 */
export function _setUserId(id) {
  _uid = id != null ? String(id) : null;
}

/** Return the current user id (or null for guests). */
export function _getUserId() {
  return _uid;
}

function prefix(key) {
  return _uid ? `u${_uid}_${key}` : `guest_${key}`;
}

export const userStorage = {
  /** Read a raw string value. */
  get(key) {
    try { return localStorage.getItem(prefix(key)); } catch { return null; }
  },

  /** Write a raw string value. */
  set(key, value) {
    try { localStorage.setItem(prefix(key), value); } catch {}
  },

  /** Read and JSON-parse a value. Returns null on missing / parse error. */
  getJSON(key) {
    try {
      const raw = localStorage.getItem(prefix(key));
      return raw !== null ? JSON.parse(raw) : null;
    } catch { return null; }
  },

  /** JSON-stringify and write a value. */
  setJSON(key, value) {
    try { localStorage.setItem(prefix(key), JSON.stringify(value)); } catch {}
  },

  /** Remove a value. */
  remove(key) {
    try { localStorage.removeItem(prefix(key)); } catch {}
  },
};
